/**
 * The WebAudio mixing graph (see docs/AUDIO.md for the full diagram):
 *
 *   deck voices ─▶ musicIn (−3 dB) ─┐
 *   tension     ─▶ intensityIn ─────┴▶ ceiling ▶ musicBus (≤ 0.35) ▶ musicDuck ─┐
 *   ambience    ─▶ ambienceIn ───────▶ ceiling ▶ ambienceBus (≤ 0.08) ──────────┤
 *   sfx         ─▶ sfxIn ────────────▶ ceiling ▶ sfxBus (≤ 0.6) ────────────────┴▶ master ▶ pauseFade ▶ limiter ▶ out
 *
 * Every write to a bus gain goes through capBusGain(), so the caps hold
 * whatever settings arrive, and each bus's content passes a soft ceiling
 * (mix.ts CEILING) first, so they also hold when many sounds stack up.
 * All changes are smoothed; nothing jumps.
 */
import type { AudioSettings } from '../contracts.ts';
import { ParamTrack, writeAutomation } from './automation.ts';
import { planDuck } from './ducking.ts';
import {
  CEILING,
  LIMITER,
  MASTER_CAP,
  MUSIC_CONTENT_GAIN,
  SMOOTHING,
  capBusGain,
  clamp01,
  computeBusGains,
  intensityLayerGain,
  softCeilingCurve,
} from './mix.ts';
import { makeLoopableNoise } from './noise.ts';

export interface NoiseBuffers {
  /** 1 s of white noise for SFX grains and the shaker. */
  white: AudioBuffer;
  /** 4 s of seamlessly looping pink noise for ambience beds. */
  pink: AudioBuffer;
}

export type BusCeilings = Record<'music' | 'ambience' | 'sfx', WaveShaperNode>;

export interface MixGraph {
  ctx: AudioContext;
  /** Melody, pads and bass of the music decks. */
  musicIn: GainNode;
  /** The tension layer (shaker + soft pad); its gain follows setIntensity. */
  intensityIn: GainNode;
  /** Where ambience layers connect (in front of the ambience ceiling). */
  ambienceIn: GainNode;
  /** Where SFX voices connect (in front of the SFX ceiling). */
  sfxIn: GainNode;
  /** The soft clippers in front of each capped bus gain. */
  ceilings: BusCeilings;
  ambienceBus: GainNode;
  sfxBus: GainNode;
  musicBus: GainNode;
  musicDuck: GainNode;
  master: GainNode;
  pauseFade: GainNode;
  limiter: DynamicsCompressorNode;
  duckTrack: ParamTrack;
  pauseTrack: ParamTrack;
  noise: NoiseBuffers;
}

const NOISE_SEEDS = { white: 0x51f00d, pink: 0x9e377b } as const;

function gainNode(ctx: AudioContext, value: number): GainNode {
  const g = ctx.createGain();
  g.gain.value = value;
  return g;
}

/**
 * A soft-ceiling stage feeding `out`. Returns its input: a 1/drive pre-gain
 * (so the WaveShaper's [−1, 1] input range covers sums up to `drive`) and
 * the shaper itself.
 */
function ceilingInto(ctx: AudioContext, out: AudioNode, curve: Float32Array<ArrayBuffer>): { input: GainNode; shaper: WaveShaperNode } {
  const input = gainNode(ctx, 1 / CEILING.drive);
  const shaper = ctx.createWaveShaper();
  shaper.curve = curve;
  shaper.oversample = 'none';
  input.connect(shaper);
  shaper.connect(out);
  return { input, shaper };
}

function noiseBuffer(ctx: AudioContext, seconds: number, colour: 'white' | 'pink'): AudioBuffer {
  const length = Math.max(1, Math.round(ctx.sampleRate * seconds));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  buffer.getChannelData(0).set(makeLoopableNoise(length, colour, NOISE_SEEDS[colour]));
  return buffer;
}

/** Build the graph. All buses start silent; call applyVolumes() to bring them up smoothly. */
export function createMixGraph(ctx: AudioContext): MixGraph {
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = LIMITER.thresholdDb;
  limiter.knee.value = LIMITER.kneeDb;
  limiter.ratio.value = LIMITER.ratio;
  limiter.attack.value = LIMITER.attackS;
  limiter.release.value = LIMITER.releaseS;
  limiter.connect(ctx.destination);

  const pauseFade = gainNode(ctx, 1);
  pauseFade.connect(limiter);
  const master = gainNode(ctx, 0);
  master.connect(pauseFade);

  const musicDuck = gainNode(ctx, 1);
  musicDuck.connect(master);
  const musicBus = gainNode(ctx, 0);
  musicBus.connect(musicDuck);
  const ambienceBus = gainNode(ctx, 0);
  ambienceBus.connect(master);
  const sfxBus = gainNode(ctx, 0);
  sfxBus.connect(master);

  const curve = softCeilingCurve();
  const musicCeiling = ceilingInto(ctx, musicBus, curve);
  const ambienceCeiling = ceilingInto(ctx, ambienceBus, curve);
  const sfxCeiling = ceilingInto(ctx, sfxBus, curve);

  const musicIn = gainNode(ctx, MUSIC_CONTENT_GAIN);
  musicIn.connect(musicCeiling.input);
  const intensityIn = gainNode(ctx, 0);
  intensityIn.connect(musicCeiling.input);

  return {
    ctx,
    musicIn,
    intensityIn,
    ambienceIn: ambienceCeiling.input,
    sfxIn: sfxCeiling.input,
    ceilings: { music: musicCeiling.shaper, ambience: ambienceCeiling.shaper, sfx: sfxCeiling.shaper },
    ambienceBus,
    sfxBus,
    musicBus,
    musicDuck,
    master,
    pauseFade,
    limiter,
    duckTrack: new ParamTrack(1),
    pauseTrack: new ParamTrack(1),
    noise: { white: noiseBuffer(ctx, 1, 'white'), pink: noiseBuffer(ctx, 4, 'pink') },
  };
}

/** Glide a param to `value` (exponential approach, no clicks). */
function glide(param: AudioParam, value: number, now: number, timeConstant: number): void {
  param.cancelScheduledValues(now);
  param.setTargetAtTime(value, now, timeConstant);
}

/** Apply volume settings: perceptual mapping, hard caps, mute. */
export function applyVolumes(g: MixGraph, settings: AudioSettings): void {
  const gains = computeBusGains(settings);
  const now = g.ctx.currentTime;
  glide(g.master.gain, Math.min(MASTER_CAP, clamp01(gains.master)), now, SMOOTHING.volume);
  glide(g.musicBus.gain, capBusGain('music', gains.music), now, SMOOTHING.volume);
  glide(g.sfxBus.gain, capBusGain('sfx', gains.sfx), now, SMOOTHING.volume);
  glide(g.ambienceBus.gain, capBusGain('ambience', gains.ambience), now, SMOOTHING.volume);
}

/** Set the tension layer from stack height 0–1 (slowly smoothed; max +3 dB). */
export function applyIntensity(g: MixGraph, intensity: number): void {
  glide(g.intensityIn.gain, intensityLayerGain(intensity), g.ctx.currentTime, SMOOTHING.intensity);
}

/** Duck the music −6 dB for 350 ms, starting now. */
export function duckMusic(g: MixGraph): void {
  const now = g.ctx.currentTime;
  writeAutomation(g.musicDuck.gain, now, planDuck(g.duckTrack, now));
}

/** Fade everything out (paused) or back in; returns when the fade will be complete (audio time). */
export function fadePause(g: MixGraph, paused: boolean, fadeS: number): number {
  const now = g.ctx.currentTime;
  writeAutomation(g.pauseFade.gain, now, g.pauseTrack.rampTo(paused ? 0 : 1, now, now, now + fadeS));
  return now + fadeS;
}
